/**
 * MOJO Swatch Hover Image Preview Controller
 * Production presentation-only image swap for product card color swatches.
 */
(function() {
  'use strict';

  function isDesktopFinePointer() {
    return window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function getCardElement(el) {
    return el.closest('[data-mojo-preview-card], .mj-elegance-card, .mj-luxury-card, .mojo-pure-card, .mojo-lux-card, .mojo-product-card-container, .resource-card, product-card, .product-card');
  }

  function getPrimaryImage(card) {
    if (!card) return null;
    return card.querySelector('[data-mojo-preview-image], .mj-lux-grid-img.main-grid-img, .mj-luxury-img.primary-img, .mojo-pure-img.primary-img, .mojo-lux-img.primary-img, .mojo-product-image-wrapper img, .resource-card__image:not(.resource-card__image--secondary), .product-media__image, img');
  }


  // Keep hover-preview stock badges in sync with the previewed sibling product.
  const badgeStates = new WeakMap();

  function imageWrapper(card) {
    return card.querySelector('.mj-elegance-img-wrapper, .mj-luxury-img-wrapper');
  }

  function updatePreviewStockBadge(card, label) {
    const wrapper = imageWrapper(card);
    if (!wrapper) return;
    const sale = wrapper.querySelector('.mj-elegance-badge, .mj-luxury-sale-badge');
    if (!badgeStates.has(card)) {
      const original = wrapper.querySelector('.mojo-stock-badge');
      badgeStates.set(card, {
        existed: !!original,
        text: original ? original.textContent : '',
        saleShifted: !!sale && sale.classList.contains('mojo-sale-badge-below-stock')
      });
    }

    let badge = wrapper.querySelector('.mojo-stock-badge');
    if (label) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'mojo-stock-badge';
        wrapper.appendChild(badge);
      }
      badge.textContent = label;
    } else if (badge) {
      badge.remove();
    }
    if (sale) sale.classList.toggle('mojo-sale-badge-below-stock', !!label);
  }

  function restorePreviewStockBadge(card) {
    const state = badgeStates.get(card);
    if (!state) return;
    const wrapper = imageWrapper(card);
    if (wrapper) {
      let badge = wrapper.querySelector('.mojo-stock-badge');
      if (state.existed) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'mojo-stock-badge';
          wrapper.appendChild(badge);
        }
        badge.textContent = state.text;
      } else if (badge) {
        badge.remove();
      }
      const sale = wrapper.querySelector('.mj-elegance-badge, .mj-luxury-sale-badge');
      if (sale) sale.classList.toggle('mojo-sale-badge-below-stock', state.saleShifted);
    }
    badgeStates.delete(card);
  }

  function handleSwatchEnter(dot) {
    if (!isDesktopFinePointer()) return;
    const previewSrc = dot.getAttribute('data-preview-src');
    if (!previewSrc) return;

    const card = getCardElement(dot);
    if (!card) return;

    const image = getPrimaryImage(card);
    if (!image) return;

    // Save original state once per preview session
    if (!image.dataset.mojoOriginalSrc) {
      image.dataset.mojoOriginalSrc = image.getAttribute('data-original-src') || image.getAttribute('src') || image.currentSrc || '';
      image.dataset.mojoOriginalSrcset = image.getAttribute('srcset') || '';
      image.dataset.mojoOriginalAlt = image.getAttribute('alt') || '';
    }

    // Safety fallback: if preview image fails to load, restore original
    image.onerror = function() {
      if (image.dataset.mojoOriginalSrc) {
        image.src = image.dataset.mojoOriginalSrc;
        if (image.dataset.mojoOriginalSrcset) {
          image.setAttribute('srcset', image.dataset.mojoOriginalSrcset);
        } else {
          image.removeAttribute('srcset');
        }
        if (image.dataset.mojoOriginalAlt) {
          image.setAttribute('alt', image.dataset.mojoOriginalAlt);
        }
      }
      restorePreviewStockBadge(card);
      card.classList.remove('is-swatch-previewing');
      image.onerror = null;
    };

    updatePreviewStockBadge(card, dot.getAttribute('data-preview-stock-label') || '');

    // Apply preview image
    image.src = previewSrc;

    const previewSrcset = dot.getAttribute('data-preview-srcset');
    if (previewSrcset) {
      image.setAttribute('srcset', previewSrcset);
    } else {
      image.removeAttribute('srcset');
    }

    const previewAlt = dot.getAttribute('data-preview-alt');
    if (previewAlt) {
      image.setAttribute('alt', previewAlt);
    }

    card.classList.add('is-swatch-previewing');
  }

  function handleSwatchLeave(container) {
    const card = getCardElement(container);
    if (!card) return;

    const image = getPrimaryImage(card);
    if (!image) return;

    if (image.dataset.mojoOriginalSrc) {
      image.onerror = null;
      image.src = image.dataset.mojoOriginalSrc;

      if (image.dataset.mojoOriginalSrcset) {
        image.setAttribute('srcset', image.dataset.mojoOriginalSrcset);
      } else {
        image.removeAttribute('srcset');
      }

      if (image.dataset.mojoOriginalAlt) {
        image.setAttribute('alt', image.dataset.mojoOriginalAlt);
      }

      delete image.dataset.mojoOriginalSrc;
      delete image.dataset.mojoOriginalSrcset;
      delete image.dataset.mojoOriginalAlt;
    }

    restorePreviewStockBadge(card);
    card.classList.remove('is-swatch-previewing');
  }

  function init() {
    // Event delegation for mouse/pointer
    document.addEventListener('pointerover', function(e) {
      const dot = e.target.closest('.mj-card-swatch-dot');
      if (dot) {
        handleSwatchEnter(dot);
      }
    });

    document.addEventListener('pointerout', function(e) {
      const container = e.target.closest('.mj-card-swatches, [data-mojo-preview-swatches]');
      if (container && (!e.relatedTarget || !container.contains(e.relatedTarget))) {
        handleSwatchLeave(container);
      }
    });

    // Keyboard accessibility (focusin / focusout)
    document.addEventListener('focusin', function(e) {
      const dot = e.target.closest('.mj-card-swatch-dot');
      if (dot) {
        handleSwatchEnter(dot);
      }
    });

    document.addEventListener('focusout', function(e) {
      const container = e.target.closest('.mj-card-swatches, [data-mojo-preview-swatches]');
      if (container && (!e.relatedTarget || !container.contains(e.relatedTarget))) {
        handleSwatchLeave(container);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
